/* nx-hero.js — the first screen's slideshow.
 *
 * Small on purpose. The picture cross-fades in CSS and the type rises in CSS; this file only
 * decides WHICH slide is current and makes those animations play again.
 *
 * ⚠ That last part is the whole reason the file is not four lines. A CSS animation does not
 * replay when its class comes back: the element already finished it once, and re-adding the class
 * changes nothing. The mega-menu shipped with exactly this fault — its column cascade ran on the
 * first open of a page and never again, and no screenshot could show it. The fix is to clear the
 * animation, force a reflow by READING a layout property, and restore it. Not
 * requestAnimationFrame, which can coalesce the two style changes into one frame and restart
 * nothing.
 */
(function () {
  if (window.__nxHeroJs) return;
  window.__nxHeroJs = true;

  var CALM = window.matchMedia('(prefers-reduced-motion: reduce)');

  function boot(root) {
    if (root.dataset.nxHeroReady) return;
    root.dataset.nxHeroReady = '1';

    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-nxhero-slide]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-nxhero-dot]'));
    if (slides.length < 2) return;

    var wait = parseInt(root.dataset.interval, 10) || 5000;
    /* `wipe` paints the next frame in behind a travelling line, `slide` pushes the frames
       sideways, `fade` cross-dissolves them. The section prints the choice; this file writes the
       parts of each that depend on the DIRECTION, which no stylesheet can know. */
    var mode = root.dataset.motion === 'fade' ? 'fade'
             : root.dataset.motion === 'slide' ? 'slide'
             : 'wipe';
    var cur = 0;
    var timer = null;
    var seen = true;
    var running = false;
    var startedAt = 0;
    var left = wait;
    var held = false;
    var covered = false;

    function replay(el) {
      if (!el) return;
      el.style.animation = 'none';
      void el.offsetWidth;
      el.style.animation = '';
    }

    /* Put a slide on one side of the stage with no animation, and COMMIT it — the read of
       offsetWidth is what makes the browser treat the next assignment as a move rather than as
       the same style change collapsed into one frame. Same rule as replay() above. */
    function park(el, pct) {
      el.style.transition = 'none';
      el.style.transform = 'translateX(' + pct + '%)';
      void el.offsetWidth;
      el.style.transition = '';
    }

    function show(n, dir) {
      if (n === cur) return;
      var out = cur;
      dir = dir === 'prev' ? 'prev' : 'next';

      /* ⚠ `is-leaving` marks the frame that is on its way out AND STILL ON SCREEN, so it is set
         for every mode — it used to be set only by the wipe, which is what let the drift snap.
         The stylesheet hangs the picture's slow zoom on `is-on` and `is-leaving` together: the
         moment `is-on` moves, an outgoing image with no second rule to name its animation loses
         it and jumps back to scale 1, in full view, for the whole length of the change. All three
         modes keep the old frame lit while it leaves — the wipe stands it still underneath, the
         push carries it out, the fade dissolves it — so all three showed the jump.
         Every other `is-leaving` rule is scoped to `.nxhero--wipe`, so nothing else moves. */
      slides.forEach(function (s) { s.classList.remove('is-leaving'); });
      slides[out].classList.add('is-leaving');

      /* ⚠ Which side a slide waits on is a property of the DIRECTION, so it cannot live in the
         stylesheet. Going forward, the next frame stands off to the right and the current one
         leaves to the left; a back-swipe reverses both. Everything off the stage is clipped. */
      if (mode === 'slide') {
        var from = dir === 'prev' ? -100 : 100;
        var to = dir === 'prev' ? 100 : -100;
        slides.forEach(function (s, i) { if (i !== out) park(s, from); });
        slides[n].style.transform = 'translateX(0)';
        slides[out].style.transform = 'translateX(' + to + '%)';
      }

      /* The wipe reveals rather than moves: the picture opens from the side the line starts on, at
         the line's own timing, and the frame being replaced stays lit underneath until it is
         covered — a wipe against an empty stage reveals nothing. `data-dir` both aims the line and
         is what keeps it off the page's first paint (the stylesheet scopes it to that attribute,
         which nothing sets until the first change). */
      if (mode === 'wipe') {
        root.dataset.dir = dir;
        /* Forward opens from the RIGHT edge — the page-turn direction — so it is the LEFT inset
           that starts at 100% and retreats. Going back is the mirror of it.
           ⚠ On the PICTURE, and only the picture. The type is sequenced by the stylesheet rather
           than cut by the line: a wipe through a sentence slices it mid-word, and for the whole
           crossing the reader has half of one line beside half of another. */
        var el = slides[n].querySelector('.nxhero__pic') || slides[n];
        el.style.transition = 'none';
        el.style.clipPath = dir === 'prev' ? 'inset(0 100% 0 0)' : 'inset(0 0 0 100%)';
        void el.offsetWidth;
        el.style.transition = '';
        el.style.clipPath = 'inset(0 0 0 0)';

        /* The rule crossing the stage is a pseudo-element, so it replays the only way one can:
           drop the class, force a reflow, put it back. Exactly what the dashes do. */
        root.classList.remove('is-wiping');
        void root.offsetWidth;
        root.classList.add('is-wiping');
      }

      cur = n;
      slides.forEach(function (s, i) { s.classList.toggle('is-on', i === n); });

      /* ⚠ The dash fills by a CSS animation on a PSEUDO-element, so there is no element to write
         `animation: none` on. Dropping the class from every dash, forcing one reflow, and putting
         it back on the current one is what replays it — and it has to be replayed, or the fill
         runs once on the first slide of a page and every dash after it is simply full. That is
         the mega-menu's fault exactly, and no screenshot of it is wrong. */
      dots.forEach(function (d) { d.classList.remove('is-on'); d.setAttribute('aria-selected', 'false'); });
      void root.offsetWidth;
      if (dots[n]) { dots[n].classList.add('is-on'); dots[n].setAttribute('aria-selected', 'true'); }

      /* ⚠ A change of slide restarts the CLOCK as well, and it has to do it here rather than in
         the callers. The dash was just refilled from zero and it is drawn against that clock: if
         the pending timer is left alone it still holds what remained of the PREVIOUS slide, and
         the next change arrives early — with the bar somewhere in the middle, saying otherwise.
         Caught by the harness, which calls show() the way nothing else does: on its own. */
      left = wait;
      resched();

      if (CALM.matches) return;
      var s = slides[n];
      replay(s.querySelector('.nxhero__pic img'));
      ['.nxhero__eyebrow', '.nxhero__name', '.nxhero__line', '.nxhero__cta'].forEach(function (sel) {
        replay(s.querySelector(sel));
      });
    }

    function next() { show((cur + 1) % slides.length, 'next'); }
    function prev() { show((cur - 1 + slides.length) % slides.length, 'prev'); }

    function tick() { timer = null; running = false; next(); }

    /* Drop whatever is pending and schedule the current slide's full time — unless something is
       holding the show, which start() decides. */
    function resched() {
      if (timer) { clearTimeout(timer); timer = null; }
      running = false;
      start();
    }

    function start() {
      stop();
      /* Nothing advances behind a hidden tab, below the fold, or under a reader: a slideshow the
         reader cannot see is animation spent on nobody, and one that moves while they are reading
         it is the single thing a slideshow can do that no reader forgives. */
      if (CALM.matches || held || covered || document.hidden || !seen) return;
      running = true;
      startedAt = Date.now();
      timer = setTimeout(tick, left);
      root.classList.remove('is-paused');
    }
    /* ⚠ Pause, not reset. What is left of this slide's time is kept, and `is-paused` freezes the
       dash at the point it had reached — so the bar and the timer resume together. A setInterval
       cannot do that: it has no remainder to keep, and the fill would carry on from where it
       stopped against a clock that had started again from the top. */
    function stop() {
      if (timer) { clearTimeout(timer); timer = null; }
      if (running) { left = Math.max(0, left - (Date.now() - startedAt)); running = false; }
      root.classList.add('is-paused');
    }

    dots.forEach(function (d, i) {
      d.addEventListener('click', function () { show(i, i > cur ? 'next' : 'prev'); });
    });

    /* ⚠ The hold zone is the COPY and the dashes — not the whole first screen. Binding it to the
       section meant a pointer parked anywhere on the photograph froze the shelf for good, and
       after using the menu that is exactly where a pointer is left: measured paused=true with the
       cursor in the middle of the picture, five seconds later still nothing, moving it away and it
       ran again. The reader who must not have the slide changed under them is the one reading the
       SENTENCE or reaching for a dash; a cursor resting on a photograph is telling us nothing.
       ⚠ And only where there is a real pointer. A tap leaves a hover behind on iOS and no
       mouseleave ever follows it, so on a phone the first tap would have stopped the slideshow
       permanently — the same trap the header's `:focus-within` fell into one file over. */
    var PTR = window.matchMedia('(hover: hover)');
    var ZONE = '.nxhero__copy, .nxhero__dots';
    function inZone(node) { return !!(node && node.closest && node.closest(ZONE)); }

    root.addEventListener('mouseover', function (e) {
      if (!PTR.matches || !inZone(e.target)) return;
      held = true; stop();
    });
    root.addEventListener('mouseout', function (e) {
      if (!PTR.matches || !inZone(e.target)) return;
      /* Crossing between the sentence and a dash, or from one slide's copy to the next one's, is
         still inside the zone — it must not read as the reader leaving. */
      if (inZone(e.relatedTarget)) return;
      held = false; start();
    });
    /* ⚠ Keyboard focus holds the show; a TAP does not. Both fire `focusin`, and treating them the
       same stopped the slideshow for good on a phone the moment a dash was tapped — focus stays on
       the dash, and nothing takes it away again. `:focus-visible` is exactly the distinction the
       browser already draws, so ask it rather than sniffing the device. */
    root.addEventListener('focusin', function () {
      var byKey = true;
      try { byKey = !!root.querySelector(':focus-visible'); } catch (e) {}
      if (!byKey) return;
      held = true; stop();
    });
    root.addEventListener('focusout', function () { held = false; start(); });
    document.addEventListener('visibilitychange', function () { document.hidden ? stop() : start(); });

    /* ⚠ A full-screen overlay is the same situation as a hidden tab, and it was not covered.
       The welcome screen arrives fifteen seconds in, opaque and edge to edge; the shelf went on
       turning behind it, so a reader who closed it landed on a slide they never saw arrive — and
       the frames in between were animation spent on nobody, which is the reason the three
       conditions above exist in the first place. `stop()` keeps what is left of the current
       slide, so the show resumes rather than restarts, exactly as it does under a pointer.
       Anything else that covers the page announces itself the same way; nx-hero needs to know
       nothing about what did it. */
    document.addEventListener('nx:overlay', function (e) {
      covered = !!(e && e.detail && e.detail.open);
      if (covered) stop(); else start();
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        seen = entries[0].isIntersecting;
        seen ? start() : stop();
      }, { threshold: 0.25 }).observe(root);
    }

    /* A finger should be able to move it, and the rail is the whole picture. Horizontal only, and
       only when it beats the vertical component — or a scroll down the page would count as "next".
       The same test the gallery already uses. */
    var x0 = 0, y0 = 0;
    root.addEventListener('touchstart', function (e) {
      x0 = e.changedTouches[0].clientX; y0 = e.changedTouches[0].clientY;
    }, { passive: true });
    root.addEventListener('touchend', function (e) {
      var dx = e.changedTouches[0].clientX - x0;
      var dy = e.changedTouches[0].clientY - y0;
      if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 2) return;
      dx < 0 ? next() : prev();
    }, { passive: true });

    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    });

    /* ── the header above it ──────────────────────────────────────────────
       On a page with a hero the header sits ON the photograph, transparent,
       with light type (daytakt.css). Once the reader has scrolled past the
       picture the ground is paper again and light type would be invisible, so
       <html> is told, and the CSS puts the header back. Read in a rAF so a
       fast scroll costs one layout read a frame, not one an event. */
    var head = 0;
    var pending = false;
    /* Two numbers the CSS cannot know and must not guess: how tall the header
       really is (the hero is pulled up by exactly that, or a sliver of page
       shows under the announcement bar) and how tall the bar is (the stage is
       the screen minus it, because the header is over the picture now, not
       above it). Both are published as custom properties and recomputed on
       resize. */
    function measureHead() {
      var w = document.querySelector('.header-wrapper');
      head = w ? Math.round(w.getBoundingClientRect().height) : 100;
      var bar = document.querySelector('.announcement-bar-section, .shopify-section-group-header-group > :first-child');
      var barH = bar && bar !== w && bar.getBoundingClientRect().height < 80
        ? Math.round(bar.getBoundingClientRect().height) : 37;
      var s = document.documentElement.style;
      s.setProperty('--nxhero-head', head + 'px');
      s.setProperty('--nxhero-bar', barH + 'px');
    }
    function mark() {
      pending = false;
      var past = root.getBoundingClientRect().bottom <= head + 4;
      document.documentElement.classList.toggle('nxh-past', past);
    }
    function onScroll() {
      if (pending) return;
      pending = true;
      requestAnimationFrame(mark);
    }
    measureHead();
    mark();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () { measureHead(); mark(); }, { passive: true });

    start();

    /* For the test harness, which drives the thing rather than photographing it. */
    root.nxHero = {
      show: show, next: next, prev: prev,
      get index() { return cur; },
      count: slides.length,
      mode: mode,
      wait: wait,
      get paused() { return !running; },
    };
  }

  function scan() {
    Array.prototype.slice.call(document.querySelectorAll('[data-nx-hero]')).forEach(boot);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();
  /* The theme editor re-renders a section into the page without reloading it. */
  document.addEventListener('shopify:section:load', scan);
})();
